import { OutlineButton, PrimaryButton } from "@components/Buttons";
import { CustomTextarea } from "@components/Forms";
import { CustomSelect, CustomToggle } from "@components/UI";
import ToastConstant from "@configs/react-toastify/snackbar";
import { TOAST, CHARACTER_LENGTH } from "@constants/index";
import { cellLabels, importanceLevels, severityLevels } from "@constants/vulnerabilityPolicy";
import { useMutationData } from "@hooks/useMutationData";
import { useTheme } from "@hooks/useTheme";
import { getColorByVulnerabilityLevel } from "@utils/common";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

const AddVulnerabilityPolicyModal = ({ isOpen, onClose, onSuccess, existingPolicies = [] }) => {
  const { t } = useTranslation();
  const { colors } = useTheme();

  // Form state (store primitive string values)
  const [formData, setFormData] = useState({
    vulnerabilityLevel: "",
    importanceLevel: "",
    severity: "",
    useFlag: "Y",
    followupContent: "",
  });

  const [errors, setErrors] = useState({});

  // Check if a character (level code) already exists in the database
  const isCharacterExists = (importanceLevel, severity) => {
    const key = `${importanceLevel}_${severity}`.toUpperCase();
    const cellInfo = cellLabels[key];
    if (!cellInfo) return false;

    // Check if any existing policy has the same importance level and severity combination
    return existingPolicies.some(
      (policy) => policy.importanceLevelCd === importanceLevel && policy.weaknessCd === severity,
    );
  };

  // Get the level code for a given importance and severity combination
  const getLevelCode = (importanceLevel, severity) => {
    const key = `${importanceLevel}_${severity}`.toUpperCase();
    return cellLabels[key]?.label || "";
  };

  // Handle ESC key press
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === "Escape" && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscKey);
      // Prevent body scroll when modal is open
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscKey);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose]);

  const getFollowupByLevel = (level) => {
    switch (String(level)) {
      case "1":
        return t("vulnerability-policy.followup-level-1");
      case "2":
        return t("vulnerability-policy.followup-level-2");
      case "3":
        return t("vulnerability-policy.followup-level-3");
      case "4":
        return t("vulnerability-policy.followup-level-4");
      default:
        return "";
    }
  };

  const vulnerabilityLevelOptions = [
    { value: "1", label: "1" },
    { value: "2", label: "2" },
    { value: "3", label: "3" },
    { value: "4", label: "4" },
  ];

  // Create user-friendly labels for display while keeping original values for submission
  const getImportanceLabel = (level) => {
    switch (level) {
      case "HIGHEST":
        return t("vulnerability-policy.highest-level");
      case "HIGH":
        return t("vulnerability-policy.high-level");
      case "MEDIUM":
        return t("vulnerability-policy.medium-level");
      case "LOW":
        return t("vulnerability-policy.low-level");
      default:
        return level;
    }
  };

  const getSeverityLabel = (level) => {
    switch (level) {
      case "CRITICAL":
        return "Critical";
      case "HIGH":
        return "High";
      case "MEDIUM":
        return "Medium";
      case "LOW":
        return "Low";
      default:
        return level;
    }
  };

  const importanceOptions = importanceLevels.map((level) => ({
    value: level,
    label: getImportanceLabel(level),
  }));
  const severityOptions = severityLevels.map((level) => ({
    value: level,
    label: getSeverityLabel(level),
  }));

  // Convert primitive to option for CustomSelect value prop
  const asOption = (options, value) => {
    if (!value) return null;
    return options.find((o) => o.value === value) || null;
  };

  // Derive current mapping key and level info
  const selectedKey = useMemo(() => {
    if (!formData.importanceLevel || !formData.severity) return "";
    return `${formData.importanceLevel}_${formData.severity}`.toUpperCase();
  }, [formData.importanceLevel, formData.severity]);

  const selectedCellInfo = selectedKey ? cellLabels[selectedKey] : null;

  // Create mutation
  const createPolicyMutation = useMutationData(
    "/vulnerability_policy",
    { method: "POST" },
    {
      onSuccess: () => {
        ToastConstant(TOAST.success, t("vulnerability-policy.vulnerability-policy-create-success"));
        handleClose();
        onSuccess();
      },
      onError: (error) => {
        ToastConstant(TOAST.error, t("vulnerability-policy.vulnerability-policy-create-error"));
        console.error("Create error:", error);
      },
    },
  );

  // Auto-calculated fields based on importance and severity
  const getCalculatedFields = () => {
    if (!selectedCellInfo) {
      return { vulnerabilityLevel: "", levelCode: "", autoFollowupContent: "" };
    }

    const level = formData.vulnerabilityLevel || selectedCellInfo?.vulnerabilityLevel || "";

    return {
      vulnerabilityLevel: selectedCellInfo?.vulnerabilityLevel || "",
      levelCode: selectedCellInfo?.label || "",
      autoFollowupContent: getFollowupByLevel(level),
    };
  };

  const calculatedFields = getCalculatedFields();

  // Handle select changes: receive option object, store option.value string
  const handleSelectChange = (name, option) => {
    let value;

    // Handle both toggle values (direct string) and select options (object with value property)
    if (typeof option === "string") {
      value = option;
    } else {
      value = option?.value || "";
    }

    setFormData((prev) => {
      const next = { ...prev, [name]: value };

      if (name === "importanceLevel" || name === "severity") {
        const newKey =
          `${name === "importanceLevel" ? value : next.importanceLevel}_${name === "severity" ? value : next.severity}`.toUpperCase();
        const info = cellLabels[newKey];
        if (info && !next.vulnerabilityLevel) {
          next.vulnerabilityLevel = info.vulnerabilityLevel;
        }
        // Whenever mapping changes, keep followup bound to current vulnerability level
        const level = next.vulnerabilityLevel || info?.vulnerabilityLevel;
        next.followupContent = getFollowupByLevel(level);
      }

      if (name === "vulnerabilityLevel") {
        next.followupContent = getFollowupByLevel(value);
      }

      return next;
    });

    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    // Clear duplicate error when importance level or severity changes
    if (name === "importanceLevel" || name === "severity") {
      setErrors((prev) => ({ ...prev, duplicate: "" }));
    }
  };

  // Handle click on mapping table cell
  const handleCellClick = (importanceLevel, severity) => {
    const key = `${importanceLevel}_${severity}`.toUpperCase();
    const info = cellLabels[key];

    setFormData((prev) => {
      const level = info?.vulnerabilityLevel || prev.vulnerabilityLevel;
      return {
        ...prev,
        importanceLevel,
        severity,
        vulnerabilityLevel: level,
        followupContent: getFollowupByLevel(level),
      };
    });

    // Clear any duplicate error when a cell is clicked
    setErrors((prev) => ({ ...prev, duplicate: "" }));
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    if (!formData.vulnerabilityLevel) newErrors.vulnerabilityLevel = t("validation.required");
    if (!formData.importanceLevel) newErrors.importanceLevel = t("validation.required");
    if (!formData.severity) newErrors.severity = t("validation.required");
    if (!formData.followupContent.trim()) newErrors.followupContent = t("validation.required");

    // Check if trying to create a duplicate character
    if (formData.importanceLevel && formData.severity) {
      if (isCharacterExists(formData.importanceLevel, formData.severity)) {
        newErrors.duplicate = "duplicate"; // Just set a flag, we'll use translation in display
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Check if all three required selects are filled
  const areRequiredSelectsFilled = () => {
    return formData.vulnerabilityLevel && formData.importanceLevel && formData.severity;
  };

  // Handle form submission
  const handleSubmit = () => {
    if (!validateForm()) return;

    const finalCalculated = getCalculatedFields();

    const submitData = {
      useFlag: formData.useFlag,
      importanceLevelCd: formData.importanceLevel,
      weaknessCd: formData.severity,
      vulnerabilityLevel: formData.vulnerabilityLevel || finalCalculated.vulnerabilityLevel,
      levelCode: finalCalculated.levelCode,
      followupContent: formData.followupContent.trim() || finalCalculated.autoFollowupContent.trim(),
      db: false,
      delete: false,
    };

    createPolicyMutation.mutate(submitData);
  };

  // Handle modal close
  const handleClose = () => {
    setFormData({
      vulnerabilityLevel: "",
      importanceLevel: "",
      severity: "",
      useFlag: "Y",
      followupContent: "",
    });
    setErrors({});
    onClose();
  };

  // Build mapping grid
  const importanceOrder = ["HIGHEST", "HIGH", "MEDIUM", "LOW"];
  const severityOrder = ["CRITICAL", "HIGH", "MEDIUM", "LOW"];
  const accentBorderColor = "#00B4D8"; // Cyan-like accent for cell borders

  const renderMappingGrid = () => {
    return (
      <div
        className={`rounded-tl-xl rounded-bl-xl border-t border-l border-b ${colors.borderPaginationButton} ${colors.bgPrimary} p-4 relative`}
      >
        <div className={`text-lg font-semibold mb-3 ${colors.textVulnMatrixLabel}`}>
          {t("vulnerability-policy.severity-importance-mapping")}
        </div>
        <div className="overflow-x-auto">
          <div className={`inline-block relative rounded-xl ${colors.bgSecondary} pl-6 pt-6 pb-8 pr-10`}>
            <div className={`ml-27 mb-1 text-center text-[13px] font-medium ${colors.textVulnMatrixLabel}`}>
              {t("vulnerability-policy.severity")}
            </div>
            {/* Header Row for Severity */}
            <div className={`ml-27 grid grid-cols-4 gap-1 mb-2 text-center text-[11px] ${colors.textVulnMatrixLabel}`}>
              {severityOrder.map((sev) => (
                <div
                  key={sev}
                  className={`text-center rounded border ${colors.borderVulnMatrix} ${colors.bgPrimary} px-[19px] py-[12px]`}
                >
                  {getSeverityLabel(sev)}
                </div>
              ))}
            </div>

            {/* Grid Rows */}
            <div className="ml-9 space-y-2">
              {importanceOrder.map((imp) => (
                <div key={imp} className="flex items-center gap-2 ">
                  {/* Importance label */}
                  <div
                    className={`rounded border ${colors.textVulnMatrixLabel} ${colors.borderVulnMatrix} ${colors.bgPrimary} px-[10px] py-[12px] w-16 text-[11px]`}
                  >
                    {getImportanceLabel(imp)}
                  </div>

                  {/* Cells */}
                  <div className="grid grid-cols-4 gap-1">
                    {severityOrder.map((sev) => {
                      const key = `${imp}_${sev}`;
                      const label = cellLabels[key]?.label || "";
                      const isCharacterAlreadyExists = isCharacterExists(imp, sev);

                      const isSelected = selectedKey === key;
                      const colorLevel = formData.vulnerabilityLevel || cellLabels[key]?.vulnerabilityLevel;
                      const bgColor = isSelected ? getColorByVulnerabilityLevel(parseInt(colorLevel)) : "transparent";
                      const borderColor = isSelected ? bgColor : accentBorderColor;

                      return (
                        <button
                          type="button"
                          key={key}
                          onClick={() => !isCharacterAlreadyExists && handleCellClick(imp, sev)}
                          disabled={isCharacterAlreadyExists}
                          className={`w-22 h-10 rounded-md flex items-center justify-center text-sm transition-all duration-200 ${
                            isCharacterAlreadyExists
                              ? colors.matrixCellDisabled
                              : colors.matrixCellActive
                          }`}
                          style={{
                            backgroundColor: isCharacterAlreadyExists ? "#4B5563" : bgColor,
                            border: `1px solid ${isCharacterAlreadyExists ? "#6B7280" : borderColor}`,
                          }}
                          title={
                            isCharacterAlreadyExists
                              ? `${imp} - ${sev} (${t("vulnerability-policy.already-exists")})`
                              : `${imp} - ${sev}`
                          }
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {/* Vertical axis label */}
            <div className={`absolute left-[-10px] top-[60%] -translate-y-1/2 -rotate-90 text-[13px] font-medium ${colors.textVulnMatrixLabel}`}>
              {t("vulnerability-policy.importance-level")}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div
      data-testid="add-vulnerability-policy-modal"
      className={`fixed inset-0 ${colors.bgBlurModal} z-50 flex items-center justify-start xl:justify-center backdrop-blur-[1px] overflow-x-auto p-4`}
    >
      <div
        className={`${colors.bgModal} w-[90vw] min-w-[1200px] max-w-[1200px] max-h-[90vh] flex-shrink-0 overflow-auto rounded-lg shadow-xl flex flex-col`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className={`px-6 py-4 border-b ${colors.borderPrimary}`}>
          <div className="flex items-center justify-between">
            <h2 className={`${colors.textPrimary} text-xl font-bold`}>{t("vulnerability-policy.add-level")}</h2>
            <button onClick={onClose} className={`${colors.textNeutralMuted} ${colors.hoverTextNeutralStrong} text-2xl font-bold`}>
              ×
            </button>
          </div>
        </div>

        {/* Modal Content */}
        <div className="p-6">
          <div className={`space-y-6 rounded-xl ${colors.bgVulnMatrixPanel}`}>
            {/* Top: Mapping table and Setup side-by-side */}
            <div className="grid grid-cols-2">
              {/* Mapping Table */}
              {renderMappingGrid()}

              {/* Setup */}
              <div
                className={`rounded-tr-xl rounded-br-xl border ${colors.borderPaginationButton} ${colors.bgPrimary} p-4`}
              >
                <div className={`text-lg font-semibold mb-3 ${colors.textVulnMatrixLabel}`}>
                  {t("vulnerability-policy.vulnerability-setup")}
                </div>

                {/* Level Preview */}
                {
                  <div className="mb-4">
                    <div className="flex items-center space-x-2">
                      <div className={`text-sm font-normal ${colors.textVulnMatrixLabel}`}>
                        {t("vulnerability-policy.table-instruction")}
                      </div>
                    </div>
                  </div>
                }

                <div className="space-y-4" data-testid="add-vulnerability-policy-form">
                  {/* First row: Vulnerability Level + Whether to Use */}
                  <div className="grid grid-cols-4 gap-x-6 items-end">
                    <div className="col-span-2">
                      {/* Vulnerability Level */}
                      <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`}>
                        {t("vulnerability-policy.vulnerability-level")} <span className={colors.textRequired}>*</span>
                      </label>
                      <CustomSelect
                        label={t("vulnerability-policy.vulnerability-level")}
                        value={asOption(vulnerabilityLevelOptions, formData.vulnerabilityLevel)}
                        onChange={(opt) => handleSelectChange("vulnerabilityLevel", opt)}
                        options={vulnerabilityLevelOptions}
                        placeholder={t("vulnerability-policy.select-level")}
                        className="w-full"
                        isSearchable={false}
                        required
                      />
                    </div>
                    <div className="col-span-2">
                      {/* Whether to Use */}
                      <div className={`p-2 border ${colors.borderPrimary} rounded-lg ${colors.bgInput}`}>
                        <CustomToggle
                          title={t("vulnerability-policy.whether-to-use")}
                          value={formData.useFlag}
                          onChange={(value) => handleSelectChange("useFlag", value)}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Second row: Importance & Severity */}
                  <div className="grid grid-cols-2 gap-x-6">
                    {/* Importance Level */}
                    <div>
                      <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`}>
                        {t("vulnerability-policy.importance-level")} <span className={colors.textRequired}>*</span>
                      </label>
                      <CustomSelect
                        label={t("vulnerability-policy.importance-level")}
                        value={asOption(importanceOptions, formData.importanceLevel)}
                        onChange={(opt) => handleSelectChange("importanceLevel", opt)}
                        options={importanceOptions}
                        error={errors.importanceLevel}
                        placeholder={t("vulnerability-policy.select-level")}
                        isSearchable={false}
                        required
                      />
                    </div>

                    {/* Severity */}
                    <div>
                      <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`}>
                        {t("vulnerability-policy.severity")} <span className={colors.textRequired}>*</span>
                      </label>
                      <CustomSelect
                        label={t("vulnerability-policy.severity")}
                        value={asOption(severityOptions, formData.severity)}
                        onChange={(opt) => handleSelectChange("severity", opt)}
                        options={severityOptions}
                        error={errors.severity}
                        placeholder={t("vulnerability-policy.select-level")}
                        isSearchable={false}
                        required
                      />
                    </div>
                  </div>

                  {/* Fulfillment Content */}
                  <div>
                    <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`}>
                      {t("vulnerability-policy.fulfillment-content")}
                    </label>
                    <CustomTextarea
                      rows={4}
                      value={formData.followupContent}
                      onChange={(e) => handleSelectChange("followupContent", { value: e.target.value })}
                      title={formData.followupContent}
                      className={`w-full rounded-lg border text-sm font-medium focus:outline-none ${colors.bgInput} ${colors.borderPrimary} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary} resize-none`}
                      error={errors.followupContent}
                      maxLength={CHARACTER_LENGTH.COMMENT}
                    />
                    <p className={`text-xs ${colors.textCharacterCount} mt-1`}>{(formData.followupContent || "").length}/{CHARACTER_LENGTH.COMMENT}</p>
                  </div>

                  {/* Duplicate Error Display */}
                  {errors.duplicate && (
                    <div className={`${colors.textDanger} text-sm`}>
                        {t("vulnerability-policy.duplicate-error-message")}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className={`px-6 py-4 border-t ${colors.borderPrimary}`}>
          <div className="flex items-center justify-end space-x-3">
            <OutlineButton onClick={handleClose} disabled={createPolicyMutation.isPending}>
              {t("common.cancel-button")}
            </OutlineButton>
            <PrimaryButton
              data-testid="add-vulnerability-policy-save-button"
              onClick={handleSubmit}
              disabled={createPolicyMutation.isPending || !areRequiredSelectsFilled() || errors.duplicate}
            >
              {createPolicyMutation.isPending ? t("common.saving") : t("vulnerability-policy.add-to-list")}
            </PrimaryButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddVulnerabilityPolicyModal;
